import React, { useState } from "react"
import DineroFactory from "dinero.js"
import * as graphql from "lib/types/generated/graphql-types"
import { ReactComponent as CardIcon } from "../../../images/icons/gift-card.svg"
import { ReactComponent as ChevronDown } from "images/icons/chevron-down.svg"
import { ReactComponent as ChevronUp } from "images/icons/chevron-up.svg"
import { ReactComponent as CheckCircle } from "images/icons/circle-check.svg"

import {
  Container,
  Content,
  ExpandedSubTotalWrapper,
  ExpandedTotalWrapper,
  Footer,
  Header,
  Id,
  ImageAndDetails,
  Name,
  OrderLineTotal
} from "./OrderLine.styled"
import Money from "../../Money"
import CopyButton from "../../Ui/CopyButton"
import Operations from "./Operations/Operations"
import { Compensations } from "../../../classes/order/Compensations"
import { Refunds } from "../../../classes/order/Refunds"
import { Releases } from "../../../classes/order/Releases"
import { Cancellations } from "../../../classes/order/Cancellations"
import GiftCardProductOperations from "./GiftCardProductOperations"

type Props = {
  giftCardProductLine: graphql.OrderGiftCardProductItemRetain24
  order: graphql.Order
  orderHistory: graphql.OrderHistory
}

export const GiftCardProductLine = ({ giftCardProductLine, order, orderHistory }: Props) => {
  const [showFullSummary, setShowFullSummary] = useState<boolean>(false)
  const compensation = new Compensations(order.Compensations)
  const refund = new Refunds(order.Refunds)
  const release = new Releases(order.Releases)
  const cancellations = new Cancellations(order.Cancellations)

  const refundedGiftCardAmount = refund.giftCardProductsRefundedAmount(giftCardProductLine.id)
  const releasedGiftCard = release.orderLineReleased(giftCardProductLine.id)
  const cancelledOrder = cancellations.orderCancelled(order.id)

  if (!giftCardProductLine) return null

  const compensationTotal = compensation.orderLineTotal(giftCardProductLine.id)
  const compensationTotalFee = compensation.compensationFeeTotal()

  const getCustomerPaid = () => {
    if (releasedGiftCard || cancelledOrder) {
      return 0
    }
    return (
      giftCardProductLine.amount - compensationTotal - compensationTotalFee - refundedGiftCardAmount
    )
  }

  const showCustomerHasPaid = order.isTaxIncludedInPrice || order.Compensations.length === 0

  return (
    <Container>
      <Header onClick={() => setShowFullSummary(!showFullSummary)}>
        {showFullSummary ? <ChevronUp /> : <ChevronDown />}
      </Header>
      <Content $showFullSummary={showFullSummary} $showCustomerHasPaid={showCustomerHasPaid}>
        <ImageAndDetails>
          <CardIcon />
          <div>
            <Name>{giftCardProductLine.name}</Name>
            <Id>
              <b>Id: </b>
              <div>{giftCardProductLine.id}</div>
              <CopyButton string={giftCardProductLine.id} />
            </Id>
          </div>
        </ImageAndDetails>
        {!showFullSummary ? (
          <>
            <OrderLineTotal>
              Gift card total{" "}
              <b>
                <Money
                  amount={giftCardProductLine.amount}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              </b>
            </OrderLineTotal>
            <OrderLineTotal>
              Customer has paid{" "}
              <b>
                <CheckCircle />
                <Money
                  amount={getCustomerPaid()}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              </b>
            </OrderLineTotal>
          </>
        ) : (
          <div>
            <ExpandedTotalWrapper>
              <div>Amount</div>
              <Money
                amount={giftCardProductLine.amount}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </ExpandedTotalWrapper>
            {giftCardProductLine.emailText && (
              <ExpandedTotalWrapper>
                <div>Email text</div>
                <div>{giftCardProductLine.emailText}</div>
              </ExpandedTotalWrapper>
            )}
            {giftCardProductLine.smsText && (
              <ExpandedTotalWrapper>
                <div>SMS text</div>
                <div>{giftCardProductLine.smsText}</div>
              </ExpandedTotalWrapper>
            )}
            {giftCardProductLine.recipient && (
              <ExpandedTotalWrapper>
                <div>Recipient</div>
                <div>{giftCardProductLine.recipient}</div>
              </ExpandedTotalWrapper>
            )}
            {giftCardProductLine.referenceNumber && (
              <ExpandedTotalWrapper>
                <div>Reference</div>
                <div>{giftCardProductLine.referenceNumber}</div>
              </ExpandedTotalWrapper>
            )}
            {giftCardProductLine.templateId && (
              <ExpandedTotalWrapper>
                <div>Template Id</div>
                <div>{giftCardProductLine.templateId}</div>
              </ExpandedTotalWrapper>
            )}
            {giftCardProductLine.merchantReference && (
              <ExpandedTotalWrapper>
                <div>Merchant Reference</div>
                <div>{giftCardProductLine.merchantReference}</div>
              </ExpandedTotalWrapper>
            )}
            <ExpandedSubTotalWrapper>
              <div>Subtotal</div>
              <Money
                amount={giftCardProductLine.amount}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </ExpandedSubTotalWrapper>
          </div>
        )}
      </Content>
      {showFullSummary && (
        <Operations
          order={order}
          orderLineId={giftCardProductLine.id}
          salePriceAmount={giftCardProductLine.amount}
        />
      )}
      {showFullSummary && (
        <>
          <OrderLineTotal>
            Gift card total{" "}
            <b>
              <Money
                amount={giftCardProductLine.amount}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </b>
          </OrderLineTotal>
          <OrderLineTotal>
            Customer has paid{" "}
            <b>
              <CheckCircle />
              <Money
                amount={getCustomerPaid()}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </b>
          </OrderLineTotal>
        </>
      )}
      <Footer>
        <GiftCardProductOperations
          giftCardProductLine={giftCardProductLine}
          compensationTotal={compensationTotal}
          orderHistory={orderHistory}
          order={order}
        />
      </Footer>
    </Container>
  )
}
